import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { ChartContainer } from "@volca/ui/components/ui/chart";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { SERIES_COLORS } from "@volca/ui/tokens/chart-colors";
import { dayjs } from "@/core/utils/date";
import { useReviewSpec } from "@/profitability/review/review-spec";
import { overallMargin, pct, periodLabel } from "./format";
import {
  ALL,
  OTHER,
  type Point,
  axisOf,
  colorOf,
  everyKey,
  marginPoints,
  num,
  readableKeys,
  sane,
} from "./margin-series";
import type { TrendDataset, TrendPeriod } from "./types";

const TICK = { fontSize: 12, fill: "var(--subtle)" } as const;

/** A solid mark in the line's own colour. The chart default is a white dot. */
const Mark = ({
  cx,
  cy,
  payload,
  dataKey,
  fill,
  r,
}: {
  cx?: number;
  cy?: number;
  payload?: Point;
  dataKey?: string;
  fill?: string;
  r?: number;
}) => {
  if (typeof cx !== "number" || typeof cy !== "number") return null;
  const value = payload && dataKey ? payload[dataKey] : null;
  if (typeof value !== "number") return null;
  return <circle cx={cx} cy={cy} r={r ?? 2.5} fill={fill} />;
};

/**
 * Margin period by period. The business is the ink line. The first six
 * departments with a real margin are their own lines; the rest are one
 * Other line, so a seventh hue is never invented. The axis fits the named
 * departments' usual levels, so a spike does not flatten the rest, and a
 * line stops where it leaves the scale.
 */
export const MarginChart = ({
  periods,
  dataset,
  noun,
}: {
  periods: TrendPeriod[];
  dataset: TrendDataset;
  noun: string;
}) => {
  const metric = useReviewSpec().metric.label;
  const keys = readableKeys(periods, dataset);
  const shown = keys.slice(0, SERIES_COLORS.length);
  const folded = keys.slice(SERIES_COLORS.length);
  const foldedSet = new Set(folded);
  const data = marginPoints(periods, dataset);
  const dates = new Map(
    periods.map((period) => [period.run_id, period.start_date]),
  );
  const otherByPeriod = new Map(
    periods.map((period) => [
      period.run_id,
      overallMargin(
        (period.segments[dataset.id] ?? []).filter((row) =>
          foldedSet.has(row.key),
        ),
      ),
    ]),
  );
  const series = data.map((point) =>
    folded.length === 0 || !point.period
      ? point
      : { ...point, [OTHER]: otherByPeriod.get(point.period) ?? null },
  );
  const { domain, off } = axisOf(series, shown);
  const otherOff = series.some((point) => {
    const value = num(point, OTHER);
    return sane(value) && (value < domain[0] || value > domain[1]);
  });
  const dropped = everyKey(periods, dataset).filter(
    (key) => !keys.includes(key),
  );
  const away = [...off, ...(otherOff ? ["Other"] : []), ...dropped];
  const allLabel = `All ${dataset.plural.toLowerCase()}`;
  const target = periods
    .at(-1)
    ?.segments[dataset.id]?.find((row) => row.target !== null)?.target;
  const within = (value: number | null) =>
    value !== null && value >= domain[0] && value <= domain[1] ? value : null;
  const plot = series.map((point) => {
    const next: Point = { period: point.period };
    for (const key of [ALL, ...shown, ...(folded.length ? [OTHER] : [])])
      next[key] = within(num(point, key));
    return next;
  });
  const latestOf = (key: string) => {
    for (let index = series.length - 1; index >= 0; index -= 1) {
      const value = num(series[index]!, key);
      if (value !== null) return value;
    }
    return null;
  };
  const lines = [
    { key: ALL, label: allLabel, color: "var(--ink)", width: 2.5 },
    ...shown.map((key, index) => ({
      key,
      label: key,
      color: colorOf(index) ?? "var(--ink)",
      width: 1.75,
    })),
    ...(folded.length > 0
      ? [
          {
            key: OTHER,
            label: "Other",
            color: "var(--subtle)",
            width: 1.75,
          },
        ]
      : []),
  ];
  return (
    <Stack gap={3}>
      <ChartContainer
        className="aspect-auto h-64 w-full"
        config={Object.fromEntries(
          lines.map((line) => [
            line.key,
            { label: line.label, color: line.color },
          ]),
        )}
      >
        <LineChart
          data={plot}
          margin={{ left: 4, right: 12, top: 12, bottom: 0 }}
          accessibilityLayer
          title={`${metric} by ${noun}`}
        >
          <CartesianGrid vertical={false} stroke="var(--line)" />
          <XAxis
            dataKey="period"
            tickFormatter={(id: string) => {
              const date = dates.get(id);
              return date ? periodLabel(date, noun) : "";
            }}
            tick={TICK}
            tickLine={false}
            axisLine={false}
          />
          <YAxis
            tick={TICK}
            tickLine={false}
            axisLine={false}
            width={44}
            tickFormatter={(value: number) => pct(value, 0)}
            domain={domain}
          />
          {typeof target === "number" &&
            target >= domain[0] &&
            target <= domain[1] && (
              <ReferenceLine
                y={target}
                stroke="var(--subtle)"
                strokeDasharray="4 4"
              />
            )}
          <Tooltip
            labelFormatter={(id) => {
              const date = dates.get(String(id));
              return date ? dayjs(date).format("MMM D, YYYY") : "";
            }}
            formatter={(value, name) => [
              pct(Number(value)),
              name === ALL ? allLabel : name === OTHER ? "Other" : String(name),
            ]}
            contentStyle={{
              borderRadius: 8,
              borderColor: "var(--line)",
              fontSize: 12,
            }}
          />
          {lines.map((line) => (
            <Line
              key={line.key}
              dataKey={line.key}
              stroke={line.color}
              strokeWidth={line.width}
              dot={<Mark fill={line.color} r={2.5} />}
              activeDot={<Mark fill={line.color} r={4} />}
              isAnimationActive={false}
            />
          ))}
        </LineChart>
      </ChartContainer>
      <ul className="flex flex-wrap gap-x-3 gap-y-1" aria-label="Chart series">
        {lines.map((line) => {
          const latest = latestOf(line.key);
          return (
            <li
              key={line.key}
              className="flex items-center gap-1.5 text-xs text-ink"
            >
              <span
                aria-hidden
                className="pointer-events-none size-2 shrink-0 rounded-xs"
                style={{ backgroundColor: line.color }}
              />
              <span>{line.label}</span>
              {latest !== null && (
                <span className="text-subtle tabular-nums">{pct(latest)}</span>
              )}
            </li>
          );
        })}
      </ul>
      {folded.length > 0 && (
        <Text variant="caption" tone="muted">
          Other is the remaining {folded.length} {dataset.plural.toLowerCase()},
          as one line.
        </Text>
      )}
      {away.length > 0 && (
        <Text variant="caption" tone="muted">
          Off this scale: {away.join(", ")}.
        </Text>
      )}
    </Stack>
  );
};
